import React, { Component, ReactNode } from 'react';
import { ErrorPage } from '../../view/pages/errors/ErrorPage';

/**
 * Error boundary state
 */
export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary props
 */
export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error) => ReactNode;
  onError?: (error: Error) => void;
  resetKeys?: Array<string | number>;
  resetOnPropsChange?: boolean;
}

/**
 * Error fallback component props (for custom fallback)
 */
export interface ErrorFallbackProps {
  error: Error;
}

/**
 * ErrorBoundary component to catch React rendering errors
 * Uses class component as required by React for error boundaries
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  private resetTimeoutId: number | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Store error in state immediately so it's available in render
    return {
      hasError: true,
      error: error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({
      error,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error);
    }

    // Log error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  componentDidUpdate(
    prevProps: ErrorBoundaryProps,
    prevState: ErrorBoundaryState
  ): void {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    // Reset error if resetKeys changed
    if (hasError && prevState.hasError) {
      if (resetKeys && resetKeys.length > 0) {
        const hasResetKeyChanged = resetKeys.some(
          (key: string | number, index: number) =>
            key !== prevProps.resetKeys?.[index]
        );
        if (hasResetKeyChanged) {
          this.resetError();
        }
      }

      // Reset error if props changed and resetOnPropsChange is enabled
      if (resetOnPropsChange && this.hasPropsChanged(prevProps, this.props)) {
        this.resetError();
      }
    }
  }

  componentWillUnmount(): void {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  private hasPropsChanged(
    prevProps: ErrorBoundaryProps,
    currentProps: ErrorBoundaryProps
  ): boolean {
    // Simple comparison - can be enhanced if needed
    return JSON.stringify(prevProps) !== JSON.stringify(currentProps);
  }

  resetError = (): void => {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }

    this.setState({
      hasError: false,
      error: null,
    });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError && error) {
      if (fallback) {
        return fallback(error);
      }

      // Default fallback
      return (
        <ErrorPage 
          error={error} 
          status={500}
        />
      );
    }

    return children;
  }
}
